import { FormEvent, memo, useEffect, useState } from 'react';
import { createWebTicket } from '../../services/tickets';

type FormState = {
  asunto: string;
  empresa: string;
  nombreSolicitante: string;
  email: string;
  telefono: string;
  descripcionProblema: string;
};

const initialFormState: FormState = {
  asunto: '',
  empresa: '',
  nombreSolicitante: '',
  email: '',
  telefono: '',
  descripcionProblema: '',
};

type NewTicketProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (ticketId: string) => void;
};

const NewTicket = memo(({ isOpen, onClose, onSuccess }: NewTicketProps) => {
  const [formData, setFormData] = useState<FormState>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const ticketId = crypto.randomUUID();
      const ticketCode = `TK-${ticketId.slice(0, 8).toUpperCase()}`;
      await createWebTicket({
        id: ticketId,
        asunto: formData.asunto.trim(),
        empresa: formData.empresa.trim(),
        origen_requerimiento: 'Web',
        proveniente_web: true,
        nombre_solicitante: formData.nombreSolicitante.trim(),
        email: formData.email.trim(),
        telefono: formData.telefono.trim(),
        descripcion_problema: formData.descripcionProblema.trim(),
      });

      fetch('/api/send-ticket-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketCode,
          asunto: formData.asunto.trim(),
          empresa: formData.empresa.trim(),
          nombre: formData.nombreSolicitante.trim(),
          email: formData.email.trim(),
          telefono: formData.telefono.trim(),
          descripcion: formData.descripcionProblema.trim(),
        }),
      }).catch(() => {});

      setFormData(initialFormState);
      onClose();
      onSuccess(ticketId);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No fue posible crear el ticket. Intenta nuevamente.';
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className="modal-backdrop show"></div>
      <div className="modal d-block" tabIndex={-1} role="dialog" aria-modal="true">
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center gap-2">
                <i className="bi bi-headset"></i>
                Nuevo Ticket de Soporte
              </h5>
              <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              <form className="php-email-form" onSubmit={handleSubmit}>
                <div className="row gy-4">
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      name="asunto"
                      placeholder="Asunto"
                      value={formData.asunto}
                      onChange={(e) => setFormData((prev) => ({ ...prev, asunto: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      name="empresa"
                      placeholder="Empresa"
                      value={formData.empresa}
                      onChange={(e) => setFormData((prev) => ({ ...prev, empresa: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <input type="text" className="form-control" value="Web" readOnly aria-label="Origen del requerimiento" />
                  </div>

                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      name="nombreSolicitante"
                      placeholder="Nombre del solicitante"
                      value={formData.nombreSolicitante}
                      onChange={(e) => setFormData((prev) => ({ ...prev, nombreSolicitante: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <input
                      type="tel"
                      className="form-control"
                      name="telefono"
                      placeholder="Telefono"
                      value={formData.telefono}
                      onChange={(e) => setFormData((prev) => ({ ...prev, telefono: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="col-md-12">
                    <textarea
                      className="form-control"
                      name="descripcionProblema"
                      rows={6}
                      placeholder="Descripcion del problema"
                      value={formData.descripcionProblema}
                      onChange={(e) => setFormData((prev) => ({ ...prev, descripcionProblema: e.target.value }))}
                      required
                    ></textarea>
                  </div>

                  <div className="col-md-12 text-center">
                    {errorMessage && <div className="error-message d-block">{errorMessage}</div>}
                    <button type="submit" className="ticket-submit-btn" disabled={isSubmitting}>
                      <i className={`bi ${isSubmitting ? 'bi-hourglass-split' : 'bi-send-fill'}`}></i>
                      {isSubmitting ? 'Enviando...' : 'Crear Ticket'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

NewTicket.displayName = 'NewTicket';

export default NewTicket;
